import React, { useState } from 'react';
import { getPlatformClass } from '../../utils/platformUtils';
import PlatformText from '../PlatformText';
import PlatformButton from '../PlatformButton';
import './SmartProductTable.css';

const SmartProductTable = ({
  products = [],
  onEdit,
  onDelete,
  className = '',
  ...props
}) => {
  const platformClass = getPlatformClass();
  const isMobilePlatform = platformClass.includes('ios') || platformClass.includes('android') || platformClass.includes('pwa') || window.innerWidth <= 768;

  // Sorting state
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  // Expanded rows state
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Toggle row expansion
  const toggleRowExpansion = (productId) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(productId)) {
      newExpandedRows.delete(productId);
    } else {
      newExpandedRows.add(productId);
    }
    setExpandedRows(newExpandedRows);
  };

  // Sort products
  const sortedProducts = [...products].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];

    // Handle nested fields
    if (sortField === 'totalStock') {
      if (a.hasVariants) {
        aValue = a.variants?.reduce((sum, v) => sum + (v.currentStock || 0), 0) || 0;
      } else {
        aValue = a.currentStock || 0;
      }
      if (b.hasVariants) {
        bValue = b.variants?.reduce((sum, v) => sum + (v.currentStock || 0), 0) || 0;
      } else {
        bValue = b.currentStock || 0;
      }
    } else if (sortField === 'variantCount') {
      aValue = a.hasVariants ? (a.variants?.length || 0) : 0;
      bValue = b.hasVariants ? (b.variants?.length || 0) : 0;
    }

    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  // Get stock status for a product
  const getStockStatus = (product) => {
    if (product.hasVariants) {
      // Complex product - check all variants
      if (!product.variants || product.variants.length === 0) {
        return { status: 'no-variants', color: '#6b7280' };
      }

      product.variants.reduce((sum, v) => sum + (v.currentStock || 0), 0);
      const hasLowStock = product.variants.some(v => (v.currentStock || 0) <= (v.minStock || 0));
      const hasOutOfStock = product.variants.some(v => (v.currentStock || 0) <= 0);

      if (hasOutOfStock) {
        return { status: 'out-of-stock', color: '#ef4444' };
      } else if (hasLowStock) {
        return { status: 'low-stock', color: '#f59e0b' };
      } else {
        return { status: 'in-stock', color: '#10b981' };
      }
    } else {
      // Simple product
      if (product.currentStock <= 0) {
        return { status: 'out-of-stock', color: '#ef4444' };
      } else if (product.currentStock <= product.minStock) {
        return { status: 'low-stock', color: '#f59e0b' };
      } else {
        return { status: 'in-stock', color: '#10b981' };
      }
    }
  };

  // Get variant availability summary for products with variants
  const getVariantAvailability = (product) => {
    if (!product.hasVariants || !product.variants || product.variants.length === 0) {
      return null;
    }

    const totalVariants = product.variants.length;
    const availableVariants = product.variants.filter(v => (v.currentStock || 0) > 0).length;
    
    return {
      available: availableVariants,
      total: totalVariants,
      text: `${availableVariants}/${totalVariants} variants available`,
      hasStock: availableVariants > 0
    };
  };

  // Get stock display text based on product type
  const getStockDisplay = (product) => {
    if (product.hasVariants) {
      const availability = getVariantAvailability(product);
      return availability ? availability.text : 'No variants';
    } else {
      return product.currentStock || 0;
    }
  };

  // Get price range for complex products
  const getPriceRange = (product) => {
    if (product.hasVariants && product.variants && product.variants.length > 0) {
      const prices = product.variants.map(v => v.price || 0);
      const minPrice = Math.min(...prices);
      const maxPrice = Math.max(...prices);
      return minPrice === maxPrice ? `₹${minPrice}` : `₹${minPrice} - ₹${maxPrice}`;
    } else {
      return `₹${product.price || 0}`;
    }
  };

  // Desktop table view
  const renderDesktopTable = () => (
    <div className="smart-product-table-container">
      <table className="smart-product-table">
        <thead>
          <tr>
            <th onClick={() => handleSort('name')} className="sortable-header">
              Product Name
              {sortField === 'name' && (
                <span className="sort-indicator">
                  {sortDirection === 'asc' ? '↑' : '↓'}
                </span>
              )}
            </th>
            <th onClick={() => handleSort('category')} className="sortable-header">
              Category
              {sortField === 'category' && (
                <span className="sort-indicator">
                  {sortDirection === 'asc' ? '↑' : '↓'}
                </span>
              )}
            </th>
            <th onClick={() => handleSort('totalStock')} className="sortable-header">
              Stock
              {sortField === 'totalStock' && (
                <span className="sort-indicator">
                  {sortDirection === 'asc' ? '↑' : '↓'}
                </span>
              )}
            </th>
            <th>Price</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {sortedProducts.map((product) => {
            const priceRange = getPriceRange(product);
            const isExpanded = expandedRows.has(product.id);

            return (
              <React.Fragment key={product.id}>
                <tr className={`product-row ${product.hasVariants ? 'complex-product' : 'simple-product'}`}>
                  <td>
                    <div className="product-name-cell">
                      {product.hasVariants && (
                        <button
                          onClick={() => toggleRowExpansion(product.id)}
                          className="expand-button"
                          aria-label={isExpanded ? 'Collapse variants' : 'Expand variants'}
                        >
                          {isExpanded ? '▼' : '▶'}
                        </button>
                      )}
                      <div>
                        <PlatformText variant="body" className="product-name">
                          {product.name}
                          {product.hasVariants && (
                            <span className="variants-indicator"> (Variants)</span>
                          )}
                        </PlatformText>
                        {product.description && (
                          <PlatformText variant="caption" className="product-description">
                            {product.description}
                          </PlatformText>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <PlatformText variant="body" className="category-text">
                      {product.category || 'Uncategorized'}
                    </PlatformText>
                  </td>
                  <td>
                    <PlatformText 
                      variant="body" 
                      className={`stock-text ${product.hasVariants ? 'variant-availability' : ''}`}
                    >
                      {getStockDisplay(product)}
                    </PlatformText>
                  </td>
                  <td>
                    <PlatformText variant="body" className="price-text">
                      {priceRange}
                    </PlatformText>
                  </td>
                  <td>
                    <div className="action-buttons">
                      {onEdit && (
                        <PlatformButton
                          onClick={() => onEdit(product)}
                          className="edit-button"
                          size="small"
                          variant="outline"
                        >
                          Edit
                        </PlatformButton>
                      )}
                      {onDelete && (
                        <PlatformButton
                          onClick={() => onDelete(product.id)}
                          className="delete-button"
                          size="small"
                          variant="outline"
                        >
                          Delete
                        </PlatformButton>
                      )}
                    </div>
                  </td>
                </tr>
                {isExpanded && product.hasVariants && product.variants && product.variants.length > 0 && (
                  <tr className="variants-row">
                    <td colSpan="5">
                      <div className="variants-container">
                        <PlatformText variant="h4" className="variants-title">
                          Variants
                        </PlatformText>
                        <div className="variants-grid">
                          {product.variants.map((variant, index) => (
                            <div key={variant.id || index} className="variant-card">
                              <div className="variant-header">
                                <PlatformText variant="body" className="variant-name">
                                  {variant.variantName || `${variant.size} - ${variant.color}${variant.thickness ? ` - ${variant.thickness}` : ''}`}
                                </PlatformText>
                                <PlatformText variant="caption" className="variant-sku">
                                  SKU: {variant.sku}
                                </PlatformText>
                              </div>
                              <div className="variant-details">
                                <div className="variant-info-inline">
                                  <span className="variant-stock-inline">
                                    <span className="label">Stock:</span>
                                    <span className="value">{variant.currentStock || 0}</span>
                                  </span>
                                  <span className="variant-price-inline">
                                    <span className="label">Price:</span>
                                    <span className="value">₹{variant.price || 0}</span>
                                  </span>
                                </div>
                              </div>
                              <div className="variant-actions">
                                {/* Stock operations removed - use dedicated Stock In/Out pages */}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  // Mobile card view
  const renderMobileCards = () => {
    return (
      <div className="smart-product-cards">
      {sortedProducts.map((product) => {
        const stockStatus = getStockStatus(product);
        const priceRange = getPriceRange(product);
        const isExpanded = expandedRows.has(product.id);

        return (
          <div key={product.id} className={`product-card ${product.hasVariants ? 'complex-product' : 'simple-product'}`}>
            <div className="card-header">
              <div className="card-title">
                <div className="title-row">
                  <div className="title-left">
                    <PlatformText variant="h4" className="product-name">
                      {product.name}
                    </PlatformText>
                    {product.hasVariants && (
                      <span className="variants-indicator">(Variants)</span>
                    )}
                  </div>
                  <div className="title-right">
                    {!product.hasVariants && (
                      <span 
                        className="stock-status-badge mobile-status"
                        style={{ backgroundColor: stockStatus.color }}
                      >
                        {stockStatus.status.replace('-', ' ').toUpperCase()}
                      </span>
                    )}
                    {product.hasVariants && (
                      <button
                        onClick={() => toggleRowExpansion(product.id)}
                        className="expand-button"
                        aria-label={isExpanded ? 'Collapse variants' : 'Expand variants'}
                      >
                        {isExpanded ? '▼' : '▶'}
                      </button>
                    )}
                  </div>
                </div>
                {product.description && (
                  <PlatformText variant="caption" className="product-description">
                    {product.description}
                  </PlatformText>
                )}
              </div>
            </div>

            <div className="card-details">
              <div className="detail-row-main">

                <div className="detail-item">
                  <PlatformText variant="caption" className="label">
                    Stock
                  </PlatformText>
                  <PlatformText 
                    variant="body" 
                    className={`value ${product.hasVariants ? 'variant-availability' : ''}`}
                  >
                    {getStockDisplay(product)}
                  </PlatformText>
                </div>
                <div className="detail-item">
                  <PlatformText variant="caption" className="label">
                    Price
                  </PlatformText>
                  <PlatformText variant="body" className="value">
                    {priceRange}
                  </PlatformText>
                </div>
              </div>
            </div>

            <div className="card-actions">
              {onEdit && (
                <PlatformButton
                  onClick={() => onEdit(product)}
                  className="edit-button"
                  size="small"
                  variant="outline"
                >
                  Edit
                </PlatformButton>
              )}
              {onDelete && (
                <PlatformButton
                  onClick={() => onDelete(product.id)}
                  className="delete-button"
                  size="small"
                  variant="outline"
                >
                  Delete
                </PlatformButton>
              )}
            </div>

            {isExpanded && product.hasVariants && product.variants && product.variants.length > 0 && (
              <div className="variants-section">
                <PlatformText variant="h4" className="variants-title">
                  Variants
                </PlatformText>
                <div className="variants-list">
                  {product.variants.map((variant, index) => (
                    <div key={variant.id || index} className="variant-item">
                      <div className="variant-header-mobile">
                        <PlatformText variant="body" className="variant-name">
                          {variant.variantName || [variant.size, variant.color, variant.thickness, variant.other].filter(Boolean).join(' - ')}
                        </PlatformText>
                        <PlatformText variant="caption" className="variant-sku">
                          {variant.sku}
                        </PlatformText>
                      </div>
                      <div className="variant-stats-mobile">
                        <div className="variant-stat-item stock-stat">
                          <PlatformText variant="caption" className="stat-label">
                            Stock
                          </PlatformText>
                          <PlatformText variant="body" className="stat-value">
                            {variant.currentStock || 0}
                          </PlatformText>
                        </div>
                        <div className="variant-stat-item price-stat">
                          <PlatformText variant="caption" className="stat-label">
                            Price
                          </PlatformText>
                          <PlatformText variant="body" className="stat-value">
                            ₹{variant.price || 0}
                          </PlatformText>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
    );
  }

  return (
    <div className={`smart-product-table-wrapper ${platformClass} ${className}`} {...props}>
      {isMobilePlatform ? renderMobileCards() : renderDesktopTable()}
    </div>
  );
};

export default SmartProductTable; 